import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Link, Typography } from '@mui/material';
import type { JSX } from 'react';

import Skeleton from '~/components/common/Skeleton';
import { formatLastCat1Test, formatLastCat5Test } from '~/components/compliance/formatters';
import { targets } from '~/components/contracts/constants';
import { colDefTemplate } from '~/components/table/colDefTemplate';
import { yesOrNo } from '~/components/utils/booleanToText';
import { ElevatorTypeEnum } from '~/data/generated/model';
import { useServiceProvider } from '~/data/hooks/useServiceProvider';
import type { CarInProperty } from '~/data/schemas/car';
import { callbackTypeValues } from '~/data/schemas/ticket';
import { type LocaleTerms } from '~/hooks/useLocaleTerms';
import {
  formatDecimal,
  formatDecimalTwo,
  formatNumber,
  formatPercent,
  formatSecondsToDays,
} from '~/utils/format';
import { buildUrlForPropertyTickets } from '~/utils/urlBuilder';

// Keyed by the generated `ElevatorTypeEnum` so the compiler flags any value the API
// can return but we don't label — the old `CarTypeOptions` map covered a stale subset,
// so values like `mrl-traction` rendered blank. Also drives the Device Type form
// options and the elevator detail page's header.
export const ELEVATOR_TYPE_LABELS: Record<ElevatorTypeEnum, string> = {
  [ElevatorTypeEnum.Traction]: 'Traction',
  [ElevatorTypeEnum.Hydro]: 'Hydro',
  [ElevatorTypeEnum.Other]: 'Other',
  [ElevatorTypeEnum.escalator]: 'Escalator',
  [ElevatorTypeEnum['hydraulic-passenger']]: 'Hydraulic Passenger',
  [ElevatorTypeEnum['hydraulic-service']]: 'Hydraulic Service',
  [ElevatorTypeEnum['mrl-traction']]: 'MRL Traction',
  [ElevatorTypeEnum['geared-passenger']]: 'Geared Passenger',
  [ElevatorTypeEnum['geared-traction']]: 'Geared Traction',
  [ElevatorTypeEnum['gearless-traction']]: 'Gearless Traction',
  [ElevatorTypeEnum['gearless-passenger']]: 'Gearless Passenger',
  [ElevatorTypeEnum['wheelchair-lift']]: 'Wheelchair Lift',
  [ElevatorTypeEnum.dumbwaiter]: 'Dumbwaiter',
  [ElevatorTypeEnum['passenger-service']]: 'Passenger/Service',
  [ElevatorTypeEnum['high-rise-passenger']]: 'High Rise Passenger',
  [ElevatorTypeEnum['low-rise-passenger']]: 'Low Rise Passenger',
  [ElevatorTypeEnum.passenger]: 'Passenger',
  [ElevatorTypeEnum.freight]: 'Freight',
};

const ServiceProviderCell = ({ row }: { row: CarInProperty }) => {
  const { data, isLoading } = useServiceProvider(row.serviceProviderId);
  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" height={'100%'}>
        <Skeleton sx={{ width: '100%', maxHeight: 35 }} />
      </Box>
    );
  }
  return data?.name ?? '-';
};

export type BuildCarColumnsProps = {
  propertyId: string;
  canEdit: boolean;
  getActionsEl: (car: CarInProperty) => JSX.Element[];
  terms: LocaleTerms;
  /** Where the elevator's own page lives. */
  carDetailPath?: (carId: string) => string;
};

/**
 * The elevator table's column set, shared by the property Elevators tab and the invoice
 * detail page's "Elevators serviced" section, so neither gets a hand-rolled subset.
 * Pass `canEdit: false` (and a no-op `getActionsEl`) on read-only surfaces.
 */
export const buildCarColumns = ({
  propertyId,
  canEdit,
  getActionsEl,
  terms,
  carDetailPath,
}: BuildCarColumnsProps) => {
  const columns = colDefTemplate<CarInProperty>({
    options: {
      autoWidth: true,
    },
    cols: [
      // Never shown — both surfaces hide it through `columnVisibilityModel`. It exists so
      // the grid can be filtered on it: the property page's `?carId=` deep link sets a filter
      // item on this field, and MUI drops a filter whose column it cannot find.
      {
        field: 'id',
        headerName: 'ID',
      },
      {
        field: 'ahjId',
        headerName: 'License',
      },
      {
        field: 'name',
        headerName: `${terms.unit} Name`,
        // A real anchor, so the cell can be opened in a new tab and the URL is the
        // destination rather than one the app converts.
        ...(carDetailPath
          ? { renderCell: ({ row }) => <Link href={carDetailPath(row.id)}>{row.name}</Link> }
          : {}),
      },
      {
        field: 'serviceProvider',
        headerName: 'Service Provider',
        renderCell: ({ row }) => {
          return <ServiceProviderCell row={row} />;
        },
      },
      {
        field: 'criticalUnit',
        headerName: `Critical ${terms.unit}`,
        type: 'boolean',
        renderCell: ({ row }) => {
          return yesOrNo(row.criticalUnit);
        },
      },
      {
        field: 'type',
        headerName: 'Device Type',
        renderCell: ({ row }) => {
          return ELEVATOR_TYPE_LABELS[row.elevatorType ?? 'Other'];
        },
      },
      {
        field: 'installationYear',
        headerName: 'Installation Year',
        type: 'number',
        renderCell: ({ row }) => {
          return row.installationYear ? row.installationYear : '-';
        },
      },
      {
        field: 'modernizationYear',
        headerName: 'Modernization Year',
        type: 'number',
        renderCell: ({ row }) => {
          return row.modernizationYear ? row.modernizationYear : '-';
        },
      },
      {
        field: 'maintenanceVisits',
        headerName: 'Maintenance Visits',
        type: 'number',
        renderCell: ({ row }) => {
          const value = row.maintenanceVisits ?? 0;
          const url = buildUrlForPropertyTickets({
            propertyId,
            carId: row.id,
            entryType: ['visit'],
          });
          return (
            <Link sx={{ color: 'inherit', textDecorationColor: 'inherit' }} href={url}>
              {formatNumber(value)}
            </Link>
          );
        },
      },
      {
        field: 'maintenanceFrequency',
        headerName: 'Maintenance Frequency',
        flex: 1,
        minWidth: 175,
        renderCell: ({ row }) => {
          return row.maintenanceFrequency;
        },
      },
      {
        field: 'maintenanceHours',
        headerName: 'Maintenance Hours',
        flex: 1,
        minWidth: 175,
        renderCell: ({ row }) => {
          return row.maintenanceHours ? formatDecimalTwo(row.maintenanceHours) : '-';
        },
      },
      {
        field: 'maintenanceCompletion',
        headerName: 'Maint. Completion',
        type: 'number',
        renderCell: ({ row }) => {
          const value = row.maintenanceCompletion ?? 0;
          const isGood = value >= targets.maintenanceCompletion;

          const url = buildUrlForPropertyTickets({
            propertyId,
            carId: row.id,
            entryType: ['visit'],
          });

          return (
            <Box display="flex" alignItems="center" width="100%" height="100%" gap={0.5}>
              {isGood ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
              ) : (
                <WarningIcon sx={{ fontSize: 16 }} color="warning" />
              )}
              <Link sx={{ color: 'inherit', textDecorationColor: 'inherit' }} href={url}>
                <Typography variant="body2">{formatPercent(value)}</Typography>
              </Link>
            </Box>
          );
        },
      },
      {
        field: 'callbacks',
        headerName: terms.callbacks,
        type: 'number',
        renderCell: ({ row }) => {
          const value = row.callbacks ?? 0;

          const url = buildUrlForPropertyTickets({
            propertyId,
            carId: row.id,
            entryType: callbackTypeValues,
          });
          return (
            <Link sx={{ color: 'inherit', textDecorationColor: 'inherit' }} href={url}>
              {formatNumber(value)}
            </Link>
          );
        },
      },

      {
        field: 'callbackRate',
        headerName: terms.callbackRate,
        type: 'number',
        renderCell: ({ row }) => {
          const value = row.callbackRate ?? 0;
          const isGood = value <= row.regularCallbackRateTarget;

          const url = buildUrlForPropertyTickets({
            propertyId,
            carId: row.id,
            entryType: callbackTypeValues,
          });

          return (
            <Box display="flex" alignItems="center" width="100%" height="100%" gap={0.5}>
              {isGood ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
              ) : (
                <WarningIcon sx={{ fontSize: 16 }} color="warning" />
              )}
              <Link sx={{ color: 'inherit', textDecorationColor: 'inherit' }} href={url}>
                <Typography variant="body2">{formatDecimal(value)}</Typography>
              </Link>
            </Box>
          );
        },
      },

      {
        field: 'emergencyCalls',
        headerName: 'Emergency Calls',
        type: 'number',
        renderCell: ({ row }) => {
          const value = row.emergencyCalls ?? 0;

          const url = buildUrlForPropertyTickets({
            propertyId,
            carId: row.id,
            entryType: ['emergency'],
          });
          return (
            <Link sx={{ color: 'inherit', textDecorationColor: 'inherit' }} href={url}>
              {formatNumber(value)}
            </Link>
          );
        },
      },
      {
        field: 'emergencyCallbackRate',
        headerName: terms.emergencyCallbackRate,
        type: 'number',
        renderCell: ({ row }) => {
          const value = row.emergencyCallbackRate ?? 0;
          const isGood = value <= row.emergencyCallbackRateTarget;
          const url = buildUrlForPropertyTickets({
            propertyId,
            carId: row.id,
            entryType: ['emergency'],
          });
          return (
            <Box display="flex" alignItems="center" width="100%" height="100%" gap={0.5}>
              {isGood ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
              ) : (
                <WarningIcon sx={{ fontSize: 16 }} color="warning" />
              )}
              <Link sx={{ color: 'inherit', textDecorationColor: 'inherit' }} href={url}>
                <Typography variant="body2">{formatDecimal(value)}</Typography>
              </Link>
            </Box>
          );
        },
      },
      {
        field: 'meanTimeBetweenCallbackSecs',
        headerName: 'MTBC',
        type: 'number',
        renderCell: ({ row }) => {
          if (!row.meanTimeBetweenCallbackSecs) {
            return '-';
          }

          const label = formatSecondsToDays(row.meanTimeBetweenCallbackSecs);
          const url = buildUrlForPropertyTickets({
            propertyId,
            carId: row.id,
            entryType: callbackTypeValues,
          });
          return (
            <Link sx={{ color: 'inherit', textDecorationColor: 'inherit' }} href={url}>
              {label}
            </Link>
          );
        },
      },
      {
        field: 'onTimeResponse',
        headerName: 'On-Time Response',
        type: 'number',
        renderCell: ({ row }) => {
          if (row.onTimeResponse === null) {
            return '-';
          }

          const value = Number(row.onTimeResponse);
          const isGood = value >= targets.onTimeResponse;

          const url = buildUrlForPropertyTickets({
            propertyId,
            carId: row.id,
            entryType: ['visit'],
          });

          return (
            <Box display="flex" alignItems="center" width="100%" height="100%" gap={0.5}>
              {isGood ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
              ) : (
                <WarningIcon sx={{ fontSize: 16 }} color="warning" />
              )}
              <Link sx={{ color: 'inherit', textDecorationColor: 'inherit' }} href={url}>
                <Typography variant="body2">{formatPercent(value)}</Typography>
              </Link>
            </Box>
          );
        },
      },

      {
        field: 'numLandings',
        headerName: 'Number of landings',
        type: 'number',
      },
      { field: 'speedFpm', headerName: 'Speed (fpm)', type: 'number' },
      { field: 'capacityLbs', headerName: 'Capacity (lbs)', type: 'number' },
      { field: 'controllerModel', headerName: 'Controller Model' },
      { field: 'driveType', headerName: 'Drive Type' },
      { field: 'lastSspiTest', headerName: 'Last SSPI Test', type: 'number' },
      {
        field: 'lastCat1Test',
        headerName: 'Last CAT-1 Test',
        renderCell: ({ row }) => {
          return formatLastCat1Test({
            value: row.lastCat1Test,
            nullValue: '-',
          });
        },
      },
      {
        field: 'lastCat5Test',
        headerName: 'Last CAT-5 Test',
        renderCell: ({ row }) => {
          // formatLastCat5Test returns "Does not apply" for non-traction lifts,
          // including the mrl/geared/gearless-traction variants the enum now adds.
          return formatLastCat5Test({
            value: row.lastCat5Test,
            nullValue: '-',
            elevatorType: row.elevatorType,
          });
        },
      },
      { field: 'numEntrances', headerName: 'Number of Entrances', type: 'number' },
      { field: 'oem', headerName: 'OEM' },
    ],
  });

  if (canEdit) {
    columns.push({
      field: 'actions',
      type: 'actions',
      getActions: ({ row: car }) => getActionsEl(car),
      width: 70,
      align: 'right',
    });
  }

  return columns;
};
